import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Landing-page error definitions.
 *
 * Maps backend codes returned by the public landing surface
 * (`GET /api/questions/sample`, `POST /api/questions/sample/check`,
 * `POST /api/transcribe`) plus client-side recorder/network codes to
 * user-friendly copy scoped to the landing demo. Mirrors the
 * `practiceErrors.ts` per-domain pattern so landing copy is independent
 * of the authenticated practice flow — anonymous visitors see softer,
 * conversion-aware messaging (e.g. rate-limit copy nudges signup).
 */
export const LANDING_ERRORS: Record<string, ErrorDefinition> = {
  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in error.code)
  // Shared across sample / check / transcribe endpoints.
  // ---------------------------------------------------------
  RATE_LIMIT_EXCEEDED: {
    title: 'Slow down a moment',
    message: "You've practiced a little too quickly.",
    action:
      'Wait a few seconds and try again, or sign up for unlimited practice.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  TRANSCRIPTION_RATE_LIMITED: {
    title: 'Slow down a moment',
    message: "You've recorded a little too quickly.",
    action:
      'Wait a few seconds and try again, or sign up for unlimited grading.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  NOT_FOUND: {
    title: 'Question unavailable',
    message:
      "We couldn't find a sample question right now. We've been notified and are looking into it.",
    action: 'Try again in a moment.',
    retryable: true,
    category: 'server',
  },

  VALIDATION_ERROR: {
    title: "Couldn't read your answer",
    message: "Your answer didn't reach our grader in a usable form.",
    action: 'Record or type your answer and try again.',
    retryable: true,
    category: 'validation',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message:
      "We had trouble loading that. We've been notified and are looking into it.",
    action: 'Try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Transcribe-specific backend codes
  // ---------------------------------------------------------
  TRANSCRIPTION_INVALID_AUDIO: {
    title: "Couldn't read your recording",
    message: "The recording looks corrupted or in a format we don't support.",
    action: 'Record again, or switch to text.',
    retryable: true,
    category: 'validation',
  },

  TRANSCRIPTION_EMPTY_RESULT: {
    title: "We didn't catch that",
    message: "We couldn't pick up any speech in that clip.",
    action: 'Speak a little louder and try again, or switch to text.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Speak louder and closer to the microphone',
      'Move to a quieter spot',
      'Check that your microphone is working',
    ],
  },

  TRANSCRIPTION_PAYMENT_REQUIRED: {
    title: 'Voice grading is offline',
    message:
      "Our transcription service has a billing issue. We've been notified and are looking into it.",
    action: 'Type your answer for now.',
    retryable: false,
    category: 'server',
  },

  TRANSCRIPTION_AUTH_ERROR: {
    title: 'Voice grading is offline',
    message:
      "We're having trouble reaching our transcription service. We've been notified and are looking into it.",
    action: 'Type your answer, or try again later.',
    retryable: true,
    retryAfterSeconds: 60,
    category: 'server',
  },

  TRANSCRIPTION_SERVICE_ERROR: {
    title: "Couldn't transcribe your recording",
    message:
      "Something went wrong while transcribing your audio. We've been notified and are looking into it.",
    action: 'Try again in a moment, or type your answer.',
    retryable: true,
    category: 'server',
  },

  TRANSCRIPTION_UNAVAILABLE: {
    title: 'Voice grading is offline',
    message:
      "Voice grading is temporarily unavailable. We've been notified and are looking into it.",
    action: 'Type your answer for now.',
    retryable: true,
    retryAfterSeconds: 30,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client recorder codes (from useAudioRecorder)
  // ---------------------------------------------------------
  permission_denied: {
    title: 'Microphone access needed',
    message: "We need to hear your answer to grade it.",
    action: 'Allow microphone access in your browser, then tap the mic again.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Click the lock icon in the address bar and allow the mic',
      'You can also switch to text instead',
    ],
  },

  recording_too_short: {
    title: 'That was quick',
    message: "We didn't get enough audio to grade.",
    action: 'Hold the mic a moment longer next time.',
    retryable: true,
    category: 'validation',
  },

  // ---------------------------------------------------------
  // Client fallback codes (lowercase, when error.code is missing
  // from the backend response or the request never reached the server).
  // ---------------------------------------------------------
  landing_load_failed: {
    title: "Couldn't load a question",
    message: 'We had trouble reaching the question pool.',
    action: 'Check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  landing_grade_failed: {
    title: "Couldn't grade your answer",
    message: 'We had trouble reaching our grader.',
    action: 'Check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  landing_transcribe_failed: {
    title: "Couldn't transcribe your recording",
    message: 'We had trouble reaching our transcription service.',
    action: 'Check your connection and try again, or switch to text.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'The landing demo needs an internet connection.',
    action: 'Reconnect and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a landing-section error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getLandingError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...LANDING_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = LANDING_ERRORS[code] ?? {
    title: 'Something went wrong',
    message:
      "An unexpected error occurred. We've been notified and are looking into it.",
    action: 'Try again, or sign up to keep practicing.',
    retryable: true,
    category: 'unknown' as const,
  };

  return { ...errorDef, code };
}
